/**
 * <BillingOverrides> — two-pane admin override surface.
 *
 * signup-billing-mvp Phase 5 Session 6 (2026-05-13).
 *
 * Left pane: searchable list of approved users (from existing
 * `GET /api/admin/users` — no new endpoint required).
 * Right pane: per-user detail with override-action buttons dispatching to
 * `POST /api/billing/admin/override` with the appropriate `kind` + `value`.
 *
 * 7 override kinds (per Phase 2 `apply_admin_override` RPC):
 *   - tier            — assign a different subscription tier
 *   - grant           — pin a custom monthly_grant_cogs (overrides cron re-derivation)
 *   - reset_subscription — wipe state, refund flag stays separate
 *   - lock            — block runs
 *   - unlock          — clear lock
 *   - set_usage_limiting — flip the per-user usage-limiting flag
 *   - set_founding_partner — toggle the founding-partner badge
 *
 * Destructive ops (reset_subscription, lock) gate through <AdminConfirmDialog>;
 * less-destructive ops use a plain prompt-then-fire flow.
 *
 * Per-user override history (admin_billing_overrides rows) is OUT OF SCOPE
 * for Session 6 — a future Session 7+ read endpoint will surface it.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, EmptyState, Skeleton, Input } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { AdminConfirmDialog } from './AdminConfirmDialog';

type OverrideKind =
  | 'tier'
  | 'grant'
  | 'reset_subscription'
  | 'lock'
  | 'unlock'
  | 'set_usage_limiting'
  | 'set_founding_partner';

interface UserRow {
  user_id: string;
  email: string | null;
  status: 'pending' | 'approved' | 'rejected';
  role: string | null;
  admin_role: string | null;
  created_at: string;
}

// Mirror of the api `EnrichedUsageBalanceRow` (subset of fields surfaced in
// the admin detail pane). `null` from the api when the target user has no
// `usage_balance` row yet.
interface AdminUsageRow {
  user_id: string;
  tier_id: string;
  tier_display_name: string;
  monthly_grant_cogs: number;
  monthly_grant_used_cogs: number;
  pct: number;
  usage_limiting_enabled: boolean;
  grant_overridden: boolean;
  locked: boolean;
  period_start: string;
  period_end: string;
  cancel_at: string | null;
  stripe_customer_id: string | null;
}

interface Envelope<T> {
  data: T;
}

interface PendingAction {
  kind: OverrideKind;
  user: UserRow;
  value: unknown;
  destructive: boolean;
  summary: React.ReactNode;
  bodyCopy: string;
}

function genIdempotencyKey(kind: OverrideKind, userId: string): string {
  return `override-${kind}-${userId}-${Date.now()}`;
}

export function BillingOverrides() {
  const queryClient = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // Plain-input draft state for tier / grant pickers in the detail pane.
  const [draftTierId, setDraftTierId] = useState('');
  const [draftGrantCogs, setDraftGrantCogs] = useState('');

  const usersQuery = useQuery<UserRow[]>({
    queryKey: ['admin-users', 'approved'],
    queryFn: async () => {
      const env = await api.get<Envelope<UserRow[]>>(
        '/api/admin/users?status=approved'
      );
      return env.data;
    },
    staleTime: 30_000,
  });

  // Per-user billing state (tier + grant + used + locked + override marker)
  // for the detail pane. Backs the Session-13 admin observability surface.
  // Re-fetches when the admin selects a new user; stays stale-while-revalidate
  // after an override fires (invalidated in `onSuccess` below).
  const usageQuery = useQuery<AdminUsageRow | null>({
    queryKey: ['admin-user-usage', selectedUserId],
    queryFn: async () => {
      if (!selectedUserId) return null;
      const env = await api.get<Envelope<AdminUsageRow | null>>(
        `/api/billing/admin/usage/${selectedUserId}`
      );
      return env.data;
    },
    enabled: !!selectedUserId,
    staleTime: 10_000,
  });

  const overrideMutation = useMutation({
    mutationFn: async (input: {
      targetUserId: string;
      kind: OverrideKind;
      value: unknown;
      idempotency_key: string;
      reason: string;
    }) => {
      const env = await api.post<Envelope<{ idempotent_replay: boolean }>>(
        '/api/billing/admin/override',
        input
      );
      return env.data;
    },
    onSuccess: (data) => {
      setActionError(null);
      setActionSuccess(
        data.idempotent_replay
          ? 'Override already applied (idempotent replay).'
          : 'Override applied.'
      );
      setPending(null);
      // Refresh: users list + the per-user usage detail pane (the admin
      // GET shipped in Session 11 of multi-persona billing E2E).
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-user-usage'] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setActionError(`${err.code ?? 'ERROR'}: ${err.message}`);
      } else if (err instanceof Error) {
        setActionError(err.message);
      } else {
        setActionError('Unknown error');
      }
      setPending(null);
    },
  });

  const filtered = useMemo(() => {
    const list = usersQuery.data ?? [];
    if (!filter.trim()) return list;
    const q = filter.toLowerCase();
    return list.filter(
      (u) =>
        (u.email ?? '').toLowerCase().includes(q) ||
        u.user_id.toLowerCase().includes(q)
    );
  }, [usersQuery.data, filter]);

  const selectedUser = useMemo(() => {
    if (!selectedUserId) return null;
    return (
      (usersQuery.data ?? []).find((u) => u.user_id === selectedUserId) ?? null
    );
  }, [selectedUserId, usersQuery.data]);

  function buildAction(
    kind: OverrideKind,
    user: UserRow,
    value: unknown,
    bodyCopy: string,
    summary: React.ReactNode,
    destructive: boolean
  ): PendingAction {
    return { kind, user, value, destructive, bodyCopy, summary };
  }

  function dispatchPending() {
    if (!pending) return;
    overrideMutation.mutate({
      targetUserId: pending.user.user_id,
      kind: pending.kind,
      value: pending.value,
      idempotency_key: genIdempotencyKey(pending.kind, pending.user.user_id),
      reason: `Admin override: ${pending.kind}`,
    });
  }

  function startTierOverride() {
    if (!selectedUser || !draftTierId.trim()) return;
    setActionSuccess(null);
    setActionError(null);
    setPending(
      buildAction(
        'tier',
        selectedUser,
        { tier_id: draftTierId.trim() },
        `Assign tier "${draftTierId.trim()}" to ${
          selectedUser.email ?? selectedUser.user_id
        }?`,
        <span>
          Tier → <span className="text-accent">{draftTierId.trim()}</span>
        </span>,
        false
      )
    );
  }

  function startGrantOverride() {
    if (!selectedUser) return;
    const parsed = parseInt(draftGrantCogs, 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setActionError('Grant must be a non-negative integer (COGS).');
      return;
    }
    setActionSuccess(null);
    setActionError(null);
    setPending(
      buildAction(
        'grant',
        selectedUser,
        { monthly_grant_cogs: parsed },
        `Pin monthly grant to ${parsed.toLocaleString('en-US')} COGS for ${
          selectedUser.email ?? selectedUser.user_id
        }? (Disables hourly cron re-derivation.)`,
        <span>
          Pinned grant →{' '}
          <span className="text-accent">
            {parsed.toLocaleString('en-US')} COGS
          </span>
        </span>,
        false
      )
    );
  }

  function startResetSubscription() {
    if (!selectedUser) return;
    setActionSuccess(null);
    setActionError(null);
    setPending(
      buildAction(
        'reset_subscription',
        selectedUser,
        {},
        `Reset subscription state for ${
          selectedUser.email ?? selectedUser.user_id
        }? This wipes their period balance and tier assignment.`,
        <span className="text-danger">
          Destructive — clears tier + period balance
        </span>,
        true
      )
    );
  }

  function startLock() {
    if (!selectedUser) return;
    setActionSuccess(null);
    setActionError(null);
    setPending(
      buildAction(
        'lock',
        selectedUser,
        {},
        `Lock ${
          selectedUser.email ?? selectedUser.user_id
        }? All future runs will be blocked at the eligibility gate.`,
        <span className="text-danger">Destructive — blocks future runs</span>,
        true
      )
    );
  }

  function startUnlock() {
    if (!selectedUser) return;
    setActionSuccess(null);
    setActionError(null);
    setPending(
      buildAction(
        'unlock',
        selectedUser,
        {},
        `Unlock ${
          selectedUser.email ?? selectedUser.user_id
        }? Future runs will be allowed (subject to balance).`,
        <span>Unlock — future runs allowed</span>,
        false
      )
    );
  }

  function startSetUsageLimiting(enabled: boolean) {
    if (!selectedUser) return;
    setActionSuccess(null);
    setActionError(null);
    setPending(
      buildAction(
        'set_usage_limiting',
        selectedUser,
        // RPC `apply_admin_override` reads `p_value_jsonb->>'enabled'` (NOT
        // `usage_limiting_enabled`) — the FP-approval path in
        // `AdminService.approveUser` is the precedent and sends `{enabled:
        // true}`. Mismatched wire-keys silently coerce to `false` (defaulted
        // COALESCE) and the override no-ops while still writing an audit row +
        // lifecycle event — the exact bug surfaced in Session 12 of the
        // multi-persona billing E2E (2026-05-26).
        { enabled },
        `Set usage-limiting=${enabled ? 'true' : 'false'} for ${
          selectedUser.email ?? selectedUser.user_id
        }? ${
          enabled
            ? 'Runs will block when allowance exhausted.'
            : 'Runs will be capture-only (no blocking).'
        }`,
        <span>
          Usage limiting →{' '}
          <span className="text-accent">
            {enabled ? 'enabled' : 'disabled'}
          </span>
        </span>,
        false
      )
    );
  }

  function startSetFoundingPartner(enabled: boolean) {
    if (!selectedUser) return;
    setActionSuccess(null);
    setActionError(null);
    setPending(
      buildAction(
        'set_founding_partner',
        selectedUser,
        // Same wire-shape requirement as `set_usage_limiting` above — RPC
        // reads `p_value_jsonb->>'enabled'`. `AdminService.approveUser` is
        // the precedent (sends `{enabled: true}` + optional
        // `monthly_grant_cogs`).
        { enabled },
        `Toggle founding-partner=${enabled ? 'true' : 'false'} for ${
          selectedUser.email ?? selectedUser.user_id
        }?`,
        <span>
          Founding partner →{' '}
          <span className="text-accent">{enabled ? 'true' : 'false'}</span>
        </span>,
        false
      )
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="billing-overrides">
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono font-semibold text-text-muted tracking-[1.4px] uppercase">
          Billing Overrides
        </span>
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="filter by email or user_id"
          data-testid="billing-overrides-filter"
        />
      </div>

      {/* Loading state */}
      {usersQuery.isLoading && (
        <div
          className="flex flex-col gap-2"
          data-testid="billing-overrides-loading"
        >
          <Skeleton variant="line" className="h-8 w-full" />
          <Skeleton variant="line" className="h-8 w-full" />
          <Skeleton variant="line" className="h-8 w-full" />
        </div>
      )}

      {/* Error state */}
      {usersQuery.isError && !usersQuery.isLoading && (
        <div
          className="px-3 py-2 bg-danger/[0.06] border border-danger/15 rounded text-danger text-xs font-mono"
          data-testid="billing-overrides-error"
        >
          Failed to load users.{' '}
          <button
            className="underline cursor-pointer"
            onClick={() => usersQuery.refetch()}
            type="button"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {usersQuery.isSuccess && filtered.length === 0 && (
        <EmptyState
          title="NO USERS FOUND"
          description={
            filter
              ? 'No users match the current filter.'
              : 'No approved users yet.'
          }
        />
      )}

      {/* Success state — two-pane layout */}
      {usersQuery.isSuccess && filtered.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-3">
          {/* Left pane: user list */}
          <div className="overflow-x-auto border border-panel-border rounded-[3px] max-h-[480px] overflow-y-auto">
            <table
              className="w-full text-xs font-mono"
              data-testid="billing-overrides-users-table"
            >
              <thead className="sticky top-0">
                <tr className="border-b border-panel-border bg-panel-bg">
                  <th className="text-left px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                    Email
                  </th>
                  <th className="text-left px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                    User ID
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr
                    key={u.user_id}
                    onClick={() => {
                      setSelectedUserId(u.user_id);
                      setActionSuccess(null);
                      setActionError(null);
                    }}
                    className={`border-b border-panel-border/40 cursor-pointer transition-colors ${
                      selectedUserId === u.user_id
                        ? 'bg-accent-dim/40'
                        : 'hover:bg-input-bg/40'
                    }`}
                    data-testid={`billing-overrides-user-row-${u.user_id}`}
                  >
                    <td className="px-3 py-2 text-text-bright">
                      {u.email ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-text-dim">
                      {u.user_id.slice(0, 8)}…
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Right pane: detail */}
          <div
            className="border border-panel-border rounded-[3px] p-3"
            data-testid="billing-overrides-detail-pane"
          >
            {!selectedUser ? (
              <EmptyState
                title="NO USER SELECTED"
                description="Pick a user from the list to apply an override."
              />
            ) : (
              <div className="flex flex-col gap-3">
                <div>
                  <span className="text-text-bright font-mono text-sm block">
                    {selectedUser.email ?? '—'}
                  </span>
                  <span className="text-text-dim font-mono text-micro">
                    {selectedUser.user_id}
                  </span>
                </div>

                {/* current usage state */}
                <div
                  className="border border-panel-border rounded-[2px] p-3 bg-panel-bg"
                  data-testid="billing-overrides-usage-panel"
                >
                  <span className="text-xs font-mono text-text-muted tracking-[1.1px] uppercase block mb-2">
                    Current usage
                  </span>
                  {usageQuery.isLoading && <Skeleton className="h-16" />}
                  {!usageQuery.isLoading && !usageQuery.data && (
                    <span className="font-mono text-xs text-text-dim">
                      No usage_balance row — user has not subscribed and has not
                      been granted an allowance yet.
                    </span>
                  )}
                  {!usageQuery.isLoading && usageQuery.data && (
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs text-text-muted">
                          Tier
                        </span>
                        <span
                          className="font-mono text-sm text-text-bright"
                          data-testid="billing-overrides-usage-tier"
                        >
                          {usageQuery.data.tier_display_name}
                          {usageQuery.data.grant_overridden && (
                            <span className="ml-2 font-mono text-micro text-warn uppercase tracking-[1px]">
                              OVERRIDE
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs text-text-muted">
                          Used / Grant
                        </span>
                        <span
                          className="font-mono text-sm text-text-bright tabular-nums"
                          data-testid="billing-overrides-usage-used"
                        >
                          {Number(
                            usageQuery.data.monthly_grant_used_cogs
                          ).toFixed(2)}{' '}
                          /{' '}
                          {Number(
                            usageQuery.data.monthly_grant_cogs
                          ).toLocaleString('en-US')}{' '}
                          <span className="text-text-muted">
                            ({Math.round(usageQuery.data.pct * 100)}%)
                          </span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs text-text-muted">
                          Limiting
                        </span>
                        <span
                          className={
                            usageQuery.data.usage_limiting_enabled
                              ? 'font-mono text-sm text-accent'
                              : 'font-mono text-sm text-text-dim'
                          }
                          data-testid="billing-overrides-usage-limiting"
                        >
                          {usageQuery.data.usage_limiting_enabled
                            ? 'enabled'
                            : 'disabled'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs text-text-muted">
                          Locked
                        </span>
                        <span
                          className={
                            usageQuery.data.locked
                              ? 'font-mono text-sm text-danger'
                              : 'font-mono text-sm text-text-dim'
                          }
                          data-testid="billing-overrides-usage-locked"
                        >
                          {usageQuery.data.locked ? 'locked' : 'unlocked'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs text-text-muted">
                          Period
                        </span>
                        <span className="font-mono text-xs text-text">
                          {new Date(
                            usageQuery.data.period_start
                          ).toLocaleDateString()}{' '}
                          –{' '}
                          {new Date(
                            usageQuery.data.period_end
                          ).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* tier override */}
                <div className="flex items-end gap-2">
                  <label className="flex flex-col gap-1 flex-1">
                    <span className="text-xs font-mono text-text-muted tracking-[1.1px] uppercase">
                      Tier id
                    </span>
                    <input
                      type="text"
                      value={draftTierId}
                      onChange={(e) => setDraftTierId(e.target.value)}
                      placeholder="e.g. principal"
                      className="px-3 py-2 bg-input-bg border border-input-border rounded-[2px] text-text-bright text-sm font-mono outline-none focus:border-accent/20"
                      data-testid="billing-overrides-tier-input"
                    />
                  </label>
                  <Button
                    variant="primary"
                    onClick={startTierOverride}
                    disabled={!draftTierId.trim()}
                    data-testid="billing-overrides-set-tier-btn"
                  >
                    Set tier
                  </Button>
                </div>

                {/* grant override */}
                <div className="flex items-end gap-2">
                  <label className="flex flex-col gap-1 flex-1">
                    <span className="text-xs font-mono text-text-muted tracking-[1.1px] uppercase">
                      Pinned grant (COGS)
                    </span>
                    <input
                      type="number"
                      min={0}
                      value={draftGrantCogs}
                      onChange={(e) => setDraftGrantCogs(e.target.value)}
                      placeholder="e.g. 1996"
                      className="px-3 py-2 bg-input-bg border border-input-border rounded-[2px] text-text-bright text-sm font-mono outline-none focus:border-accent/20"
                      data-testid="billing-overrides-grant-input"
                    />
                  </label>
                  <Button
                    variant="primary"
                    onClick={startGrantOverride}
                    disabled={!draftGrantCogs.trim()}
                    data-testid="billing-overrides-pin-grant-btn"
                  >
                    Pin grant
                  </Button>
                </div>

                {/* destructive ops */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-panel-border">
                  <Button
                    variant="danger"
                    onClick={startResetSubscription}
                    data-testid="billing-overrides-reset-btn"
                  >
                    Reset subscription
                  </Button>
                  <Button
                    variant="danger"
                    onClick={startLock}
                    data-testid="billing-overrides-lock-btn"
                  >
                    Lock
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={startUnlock}
                    data-testid="billing-overrides-unlock-btn"
                  >
                    Unlock
                  </Button>
                </div>

                {/* flag toggles */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-panel-border">
                  <Button
                    variant="secondary"
                    onClick={() => startSetUsageLimiting(true)}
                    data-testid="billing-overrides-limit-on-btn"
                  >
                    Enable usage limiting
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => startSetUsageLimiting(false)}
                    data-testid="billing-overrides-limit-off-btn"
                  >
                    Disable usage limiting
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => startSetFoundingPartner(true)}
                    data-testid="billing-overrides-fp-on-btn"
                  >
                    Set founding partner
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => startSetFoundingPartner(false)}
                    data-testid="billing-overrides-fp-off-btn"
                  >
                    Clear founding partner
                  </Button>
                </div>

                {actionError && (
                  <div
                    className="px-3 py-2 bg-danger/[0.06] border border-danger/15 rounded text-danger text-xs font-mono"
                    data-testid="billing-overrides-error-banner"
                  >
                    {actionError}
                  </div>
                )}
                {actionSuccess && (
                  <div
                    className="px-3 py-2 bg-accent-dim border border-accent/15 rounded text-accent text-xs font-mono"
                    data-testid="billing-overrides-success-banner"
                  >
                    {actionSuccess}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation gate (destructive ops use type-CONFIRM; less-destructive use plain submit) */}
      {pending && (
        <AdminConfirmDialog
          open={true}
          onClose={() => setPending(null)}
          onConfirm={dispatchPending}
          title={
            pending.destructive
              ? `Confirm — ${pending.kind}`
              : `Apply override — ${pending.kind}`
          }
          confirmLabel={
            pending.destructive ? 'Apply destructive override' : 'Apply'
          }
          dangerColor={pending.destructive}
          body={pending.bodyCopy}
          summary={pending.summary}
        />
      )}
    </div>
  );
}
